import { gql, TypedDocumentNode } from "@apollo/client";
import { PERSON_FRAGMENT, PHONE_NUMBER_FRAGMENT } from "@/fragments";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import { Person } from "@/types";
import { PageInfo, PaginationVariables } from "@/components/Pagination";
export type Query = {
  contactPostalCode: string;
  email: string;
  firstName: string;
  lastName: string;
  pgaId: string;
  phone: string;
  memberClassifications: string[];
  programTypes: string[];
  sectionIds: string[];
  statuses: string[];
};

export type PeopleQueryResponse = {
  people: { nodes: Person[]; pageInfo: PageInfo; totalCount: number };
};

export type PeopleQueryInput = Partial<Query> & {
  search?: string;
  pgaIds?: string[];
  memberClassifications?: string[];
  programTypes?: string[];
  statuses?: string[];
  sectionIds?: string[];
};

export type PersonSortField = "DISPLAY_NAME" | "PGA_ID" | "SECTION" | "PRIMARY_FACILITY";
export type SortDirection = "ASC" | "DESC";

export type PersonOrderByInput = {
  field: PersonSortField;
  direction: SortDirection;
};

export type PeopleQueryVariables = PaginationVariables & { input?: PeopleQueryInput; orderBy?: PersonOrderByInput };

export const QUICKSEARCH_OPTIONS_QUERY: TypedDocumentNode<{
  crmSections: { nodes: { id: string; name: string }[] };
  classifications: { nodes: { id: string; name: string }[] };
}> = gql`
  query quicksearchOptionsQuery {
    crmSections {
      nodes {
        id
        name
      }
    }
    classifications {
      nodes {
        id
        name
      }
    }
  }
`;

export const PEOPLE_QUERY: TypedDocumentNode<PeopleQueryResponse, PeopleQueryVariables> = gql`
  query allPeopleQuery(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeCRMPeopleInput
    $orderBy: BackofficeCRMPersonOrderByInput
  ) {
    people(first: $first, last: $last, before: $before, after: $after, input: $input, orderBy: $orderBy) {
      nodes {
        ...PersonFragment
        primarySection {
          id
          name
        }
        primaryFacility {
          id
          name
        }
      }
      ...PageInfo
    }
  }
  ${PAGE_INFO_FRAGMENT}
  ${PERSON_FRAGMENT}
`;

export const PEOPLE_MEMBERSHIP_QUERY: TypedDocumentNode<PeopleQueryResponse, PeopleQueryVariables> = gql`
  query allPeopleMembershipQuery(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $input: BackofficeCRMPeopleInput
    $orderBy: BackofficeCRMPersonOrderByInput
  ) {
    people(first: $first, last: $last, before: $before, after: $after, input: $input, orderBy: $orderBy) {
      nodes {
        id
        pgaId
        latestProgram {
          id
          status
          programType {
            id
            name
          }
          band {
            id
            createdReason
            terminatedReason
            terminatesOn
            expiresOn
            classification {
              id
              name
              description
            }
          }
        }
        ccmsSuspensions {
          id
          code
          startDate
          endDate
          reasonDescription
        }
      }
      ...PageInfo
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

export const SOME_PEOPLE_QUERY = gql`
  query GetPeople($input: BackofficeCRMPeopleInput, $first: Int) {
    people(input: $input, first: $first) {
      totalCount
      nodes {
        id
        pgaId
        firstName
        lastName
        profilePhoto
        membershipSince
        primaryFacility {
          name
          id
        }
        primarySectionAffiliation {
          section {
            name
          }
        }
        employments {
          id
          title
          isPrimary
          facility {
            name
            id
          }
          startedOn
          endedOn
        }
      }
    }
  }
`;

export type PersonContactAutofillResponse = {
  person: {
    id: string;
    email: string | null;
    phoneNumbers: { id?: string; phoneNumber: string; phoneType: string }[];
    addresses: { id: string; city: string; state: string; addressType: string }[];
  } | null;
};

export type PersonContactAutofillVariables = {
  pgaId: string;
};

export const PERSON_CONTACT_AUTOFILL_QUERY: TypedDocumentNode<
  PersonContactAutofillResponse,
  PersonContactAutofillVariables
> = gql`
  query PersonContactAutofill($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      email
      phoneNumbers {
        ...PhoneNumberFragment
      }
      addresses {
        id
        city
        state
        addressType
      }
    }
  }
  ${PHONE_NUMBER_FRAGMENT}
`;

export type BackofficeUserOption = {
  id: string;
  name: string;
  email: string;
  photo?: string | null;
  department?: string | null;
};

export type SomeUsersQueryResponse = {
  backofficeUsers: BackofficeUserOption[];
};

export type SomeUsersQueryVariables = {
  search: string;
  forImpersonation?: boolean;
};

export const SOME_USERS_QUERY: TypedDocumentNode<SomeUsersQueryResponse, SomeUsersQueryVariables> = gql`
  query GetBackofficeUsers($search: String!, $forImpersonation: Boolean = false) {
    backofficeUsers(search: $search, forImpersonation: $forImpersonation) {
      id
      name
      email
      photo
      department
    }
  }
`;

export const PERSON_WITH_SALESFORCE_ID_QUERY = gql`
  query GetPerson($input: ID) {
    person(pgaId: $input) {
      id
      firstName
      lastName
      pgaId
      primaryFacility {
        name
      }
      primarySectionAffiliation {
        section {
          name
        }
      }
      profilePhoto
    }
  }
`;

export type CreatePersonInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: {
    ignoreDuplicate?: boolean;
    phoneNumber: string;
    phoneType: string;
  };
  address?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
    countryCode: string;
    addressType: string;
    isVerified: boolean;
  };
};

export type CreatePersonResponse = {
  createPerson: { success: boolean; message: string; code: number; person: { pgaId: string } };
};

export const CREATE_PERSON_MUTATION: TypedDocumentNode<CreatePersonResponse, { input: CreatePersonInput }> = gql`
  mutation CreatePerson($input: BackofficeCreatePersonInput!) {
    createPerson(input: $input) {
      success
      message
      code
      person {
        pgaId
      }
    }
  }
`;

export const PHONE_EXISTS_QUERY: TypedDocumentNode<{ people: { totalCount: number } }, { input: string }> = gql`
  query PhoneExists($input: String!) {
    people(first: 0, input: { phone: $input }) {
      totalCount
    }
  }
`;
